import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useControllableState } from '../../hooks/useControllableState.js';
import { useId } from '../../hooks/useId.js';
import type {
  TabsActivation,
  TabsListProps,
  TabsOrientation,
  TabsPanelProps,
  TabsRootProps,
  TabsSize,
  TabsTabProps,
  TabsVariant,
} from './types.js';

interface TabsContextValue {
  value: string | undefined;
  setValue: (v: string) => void;
  orientation: TabsOrientation;
  activation: TabsActivation;
  variant: TabsVariant;
  size: TabsSize;
  baseId: string;
  registerTab: (value: string, el: HTMLButtonElement | null) => void;
  /** Map kept stable across renders — TabsList reads from it on layout
   *  effects to position the active indicator without prop drilling. */
  tabsRef: React.MutableRefObject<Map<string, HTMLButtonElement>>;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext(): TabsContextValue {
  const ctx = useContext(TabsContext);
  if (ctx === null) {
    throw new Error('Tabs.List / Tabs.Tab / Tabs.Panel must be used inside <Tabs>');
  }
  return ctx;
}

/** WAI-ARIA Tabs pattern.
 *  https://www.w3.org/WAI/ARIA/apg/patterns/tabs/
 *
 *  Architecture:
 *  - `<Tabs>` owns selection state (controlled or uncontrolled) and
 *    distributes it via context. It does not render anything itself
 *    so the consumer can compose `<List>` / `<Panel>` freely (e.g.,
 *    content between the list and the panel, or a panel outside the
 *    group for stickiness).
 *  - `<Tabs.List>` is the roving-tabindex container with `role=tablist`.
 *    Arrow keys move focus (and, in automatic mode, selection).
 *  - `<Tabs.Tab>` is a button with `role=tab`, `aria-selected`,
 *    `aria-controls`. Only the selected tab is in the Tab sequence
 *    (tabindex="0"); all others are tabindex="-1".
 *  - `<Tabs.Panel>` is `role=tabpanel` + `aria-labelledby`. */
function TabsRoot(props: TabsRootProps): ReactNode {
  const {
    value,
    defaultValue,
    onValueChange,
    orientation = 'horizontal',
    activation = 'automatic',
    variant = 'line',
    size = 'md',
    className,
    children,
  } = props;
  const [selected, setSelected] = useControllableState<string>({
    defaultValue: defaultValue ?? '',
    ...(value !== undefined ? { value } : {}),
    ...(onValueChange !== undefined ? { onChange: onValueChange } : {}),
  });
  const baseId = useId('tabs');
  const tabsRef = useRef<Map<string, HTMLButtonElement>>(new Map());

  const registerTab = useCallback((v: string, el: HTMLButtonElement | null) => {
    if (el === null) tabsRef.current.delete(v);
    else tabsRef.current.set(v, el);
  }, []);

  const ctx = useMemo<TabsContextValue>(
    () => ({
      value: selected === '' ? undefined : selected,
      setValue: setSelected,
      orientation,
      activation,
      variant,
      size,
      baseId,
      registerTab,
      tabsRef,
    }),
    [selected, setSelected, orientation, activation, variant, size, baseId, registerTab],
  );

  const rootClass = [
    'vq-tabs',
    `vq-tabs--${orientation}`,
    `vq-tabs--${variant}`,
    `vq-tabs--${size}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <TabsContext.Provider value={ctx}>
      <div className={rootClass} data-component="Tabs" data-orientation={orientation}>
        {children}
      </div>
    </TabsContext.Provider>
  );
}

interface IndicatorRect {
  readonly start: number;
  readonly size: number;
}

function TabsList(props: TabsListProps): ReactNode {
  const ctx = useTabsContext();
  const listRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState<IndicatorRect | null>(null);
  const [hasMeasured, setHasMeasured] = useState(false);
  const [indicatorReady, setIndicatorReady] = useState(false);

  useEffect(() => {
    if (!hasMeasured) return undefined;
    let secondFrame = 0;
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        setIndicatorReady(true);
      });
    });
    return () => {
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
    };
  }, [hasMeasured]);

  // Position the active indicator under the active tab. Re-measure on
  // value change, orientation flip, ResizeObserver fires (font/density),
  // and tab DOM additions. Only the indicator moves; panel content stays
  // static so the motion explains selection without adding page noise.
  //
  // The indicator is absolutely positioned INSIDE the (scrollable) list,
  // so its `left`/`top` are interpreted content-relative (they scroll
  // with the strip). `getBoundingClientRect` returns viewport-relative
  // coordinates, so we add the list's `scrollLeft`/`scrollTop` to convert
  // the visible offset back to a content offset. Without this, scrolling
  // the strip detaches the indicator from the active tab by exactly the
  // scroll distance — making clicks miss what the indicator marks.
  useLayoutEffect(() => {
    const list = listRef.current;
    const activeValue = ctx.value;
    const activeEl = activeValue !== undefined ? ctx.tabsRef.current.get(activeValue) : undefined;
    if (list === null || activeEl === undefined) {
      setIndicator(null);
      return undefined;
    }
    const measure = (): void => {
      const lRect = list.getBoundingClientRect();
      const tRect = activeEl.getBoundingClientRect();
      if (ctx.orientation === 'vertical') {
        setIndicator({
          start: tRect.top - lRect.top + list.scrollTop,
          size: tRect.height,
        });
      } else {
        setIndicator({
          start: tRect.left - lRect.left + list.scrollLeft,
          size: tRect.width,
        });
      }
      setHasMeasured(true);
    };
    measure();
    // Bring the active tab fully into view if it is partially clipped by
    // the tablist's own scroll viewport. Avoid `scrollIntoView`: when
    // this tablist sits below the fold, browsers may also scroll the
    // page, yanking the reader/writer viewport away from the article.
    scrollTabIntoListView(list, activeEl, ctx.orientation);
    const ro =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (ro !== null) {
      ro.observe(list);
      ro.observe(activeEl);
    }
    // Re-measure on scroll: the indicator's content-relative coordinates
    // do not need to change as the user scrolls (it scrolls with the
    // content), but a sub-pixel rounding drift can accumulate across many
    // scroll events on some browsers, and re-measuring keeps the marker
    // pixel-aligned with the tab's actual box.
    list.addEventListener('scroll', measure, { passive: true });
    return () => {
      ro?.disconnect();
      list.removeEventListener('scroll', measure);
    };
  }, [ctx.value, ctx.orientation, ctx.tabsRef]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      const list = listRef.current;
      if (list === null) return;
      const tabs = Array.from(
        list.querySelectorAll<HTMLButtonElement>('[role="tab"]:not([data-disabled="true"])'),
      );
      if (tabs.length === 0) return;
      const current = document.activeElement as HTMLButtonElement | null;
      const idx = current === null ? -1 : tabs.indexOf(current);
      let next = -1;
      const isHoriz = ctx.orientation === 'horizontal';
      if ((isHoriz && e.key === 'ArrowRight') || (!isHoriz && e.key === 'ArrowDown')) {
        next = idx === -1 ? 0 : (idx + 1) % tabs.length;
      } else if ((isHoriz && e.key === 'ArrowLeft') || (!isHoriz && e.key === 'ArrowUp')) {
        next = idx === -1 ? tabs.length - 1 : (idx - 1 + tabs.length) % tabs.length;
      } else if (e.key === 'Home') {
        next = 0;
      } else if (e.key === 'End') {
        next = tabs.length - 1;
      } else {
        return;
      }
      e.preventDefault();
      const target = tabs[next];
      if (target === undefined) return;
      target.focus();
      if (ctx.activation === 'automatic') {
        const v = target.dataset['vqTabValue'];
        if (v !== undefined) ctx.setValue(v);
      }
    },
    [ctx],
  );

  const indicatorStyle: CSSProperties =
    indicator !== null
      ? ctx.orientation === 'vertical'
        ? { top: `${String(indicator.start)}px`, height: `${String(indicator.size)}px` }
        : { left: `${String(indicator.start)}px`, width: `${String(indicator.size)}px` }
      : { opacity: 0 };

  return (
    <div
      ref={listRef}
      role="tablist"
      className={['vq-tabs__list', props.className].filter(Boolean).join(' ')}
      data-measured={hasMeasured ? 'true' : 'false'}
      data-indicator-ready={indicatorReady ? 'true' : 'false'}
      aria-orientation={ctx.orientation}
      aria-label={props['aria-label']}
      onKeyDown={onKeyDown}
    >
      <span className="vq-tabs__indicator" aria-hidden="true" style={indicatorStyle} />
      {props.children}
    </div>
  );
}

function scrollTabIntoListView(
  list: HTMLElement,
  tab: HTMLElement,
  orientation: TabsOrientation,
): void {
  if (orientation === 'vertical') {
    const tabStart = tab.offsetTop;
    const tabEnd = tabStart + tab.offsetHeight;
    const visibleStart = list.scrollTop;
    const visibleEnd = visibleStart + list.clientHeight;

    if (tabStart < visibleStart) {
      list.scrollTop = tabStart;
      return;
    }
    if (tabEnd > visibleEnd) {
      list.scrollTop = tabEnd - list.clientHeight;
    }
    return;
  }

  const tabStart = tab.offsetLeft;
  const tabEnd = tabStart + tab.offsetWidth;
  const visibleStart = list.scrollLeft;
  const visibleEnd = visibleStart + list.clientWidth;

  if (tabStart < visibleStart) {
    list.scrollLeft = tabStart;
    return;
  }
  if (tabEnd > visibleEnd) {
    list.scrollLeft = tabEnd - list.clientWidth;
  }
}

function TabsTab(props: TabsTabProps): ReactNode {
  const ctx = useTabsContext();
  const selected = ctx.value === props.value;
  const tabId = `${ctx.baseId}-tab-${props.value}`;
  const panelId = `${ctx.baseId}-panel-${props.value}`;

  return (
    <button
      type="button"
      role="tab"
      id={tabId}
      data-vq-tab-value={props.value}
      data-disabled={props.disabled === true ? 'true' : 'false'}
      aria-selected={selected}
      aria-controls={panelId}
      disabled={props.disabled}
      tabIndex={selected ? 0 : -1}
      className={['vq-tabs__tab', props.className].filter(Boolean).join(' ')}
      ref={(el) => {
        ctx.registerTab(props.value, el);
      }}
      onClick={() => {
        if (props.disabled !== true) ctx.setValue(props.value);
      }}
      onFocus={() => {
        if (props.disabled !== true && ctx.activation === 'automatic') {
          ctx.setValue(props.value);
        }
      }}
    >
      {props.children}
    </button>
  );
}

function TabsPanel(props: TabsPanelProps): ReactNode {
  const ctx = useTabsContext();
  const selected = ctx.value === props.value;
  const tabId = `${ctx.baseId}-tab-${props.value}`;
  const panelId = `${ctx.baseId}-panel-${props.value}`;

  if (!selected && props.keepMounted !== true) return null;

  return (
    <div
      role="tabpanel"
      id={panelId}
      aria-labelledby={tabId}
      tabIndex={0}
      hidden={!selected}
      className={['vq-tabs__panel', props.className].filter(Boolean).join(' ')}
    >
      {props.children}
    </div>
  );
}

interface TabsCompound {
  (props: TabsRootProps): ReactNode;
  List: typeof TabsList;
  Tab: typeof TabsTab;
  Panel: typeof TabsPanel;
}

/** The `Tabs` export is the root component plus attached sub-components,
 *  enabling the `<Tabs>…<Tabs.List>…<Tabs.Tab>…<Tabs.Panel>` composition
 *  pattern that's idiomatic in modern React UI libraries. */
export const Tabs: TabsCompound = Object.assign(TabsRoot, {
  List: TabsList,
  Tab: TabsTab,
  Panel: TabsPanel,
});
